import type { EditorBlock, MoveRowTarget, Section } from '@getaccept/editor-lib';
import { MoveDirection } from '@getaccept/lib-shared/src/enums/move-direction';
import type { OptionMenuItem } from '@getaccept/lib-shared/src/options-menu/types/option-menu-item';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { SourceIds } from '../types';

export class MoveHelper {
  static isMovingBetweenBlocks(direction: MoveDirection): boolean {
    return [
      MoveDirection.UpOneBlock,
      MoveDirection.ToTopBlock,
      MoveDirection.DownOneBlock,
      MoveDirection.ToBottomBlock,
    ].includes(direction);
  }

  static isOnlyRowInBlock(sourceBlock: EditorBlock, movedRowId: string) {
    return sourceBlock.content.sections
      .flatMap(section => section.rows)
      .every(row => row.id === movedRowId);
  }

  static getTargetSection(direction: MoveDirection, targetSections: Section[]): Section {
    switch (direction) {
      case MoveDirection.ToTopBlock:
      case MoveDirection.UpOneBlock:
        return targetSections[targetSections.length - 1];
      case MoveDirection.DownOneBlock:
      case MoveDirection.ToBottomBlock:
        return targetSections[0];
    }
  }

  static getTargetRowId(direction: MoveDirection, targetSection: Section): string {
    switch (direction) {
      case MoveDirection.ToTopBlock:
      case MoveDirection.UpOneBlock:
        return targetSection.rows[targetSection.rows.length - 1].id;
      case MoveDirection.DownOneBlock:
      case MoveDirection.ToBottomBlock:
        return targetSection.rows[0].id;
    }
  }

  static getMoveRowBetweenBlocksPayload(
    blocks: EditorBlock[],
    { targetContentId, direction }: MoveRowTarget
  ) {
    const targetEditorBlock = blocks.find(block => block.contentId === targetContentId);
    const targetSection = this.getTargetSection(direction, targetEditorBlock?.content?.sections);
    const targetRowId = this.getTargetRowId(direction, targetSection);
    return {
      targetContentId,
      targetSectionId: targetSection.id,
      targetRowId,
      append: direction === MoveDirection.ToTopBlock || direction === MoveDirection.UpOneBlock,
    };
  }

  static getMoveRowPayload(
    direction: MoveDirection,
    { sourceSectionId, sourceRowId }: SourceIds,
    selectedSection: Section
  ) {
    switch (direction) {
      case MoveDirection.ToTop:
        return {
          targetSectionId: sourceSectionId,
          targetRowId: selectedSection.rows[0].id,
          append: false,
        };

      case MoveDirection.Up:
        return this.moveRowUp(selectedSection, sourceRowId);

      case MoveDirection.Down:
        return this.moveRowDown(selectedSection, sourceRowId);

      case MoveDirection.ToBottom: {
        const { rows } = selectedSection;
        return {
          targetSectionId: selectedSection.id,
          targetRowId: rows[rows.length - 1].id,
          append: true,
        };
      }
    }
  }

  static moveRowUp(selectedSection: Section, selectedRowId: string) {
    const selectedIndex = selectedSection.rows.findIndex(({ id }) => id === selectedRowId);
    const previousRow = selectedSection.rows[selectedIndex - 1];

    return {
      targetSectionId: selectedSection.id,
      targetRowId: previousRow.id,
      append: false,
    };
  }

  static moveRowDown(selectedSection: Section, selectedRowId: string) {
    const selectedIndex = selectedSection.rows.findIndex(({ id }) => id === selectedRowId);
    const nextRow = selectedSection.rows[selectedIndex + 1];

    return {
      targetSectionId: selectedSection.id,
      targetRowId: nextRow.id,
      append: true,
    };
  }

  static getMoveBetweenBlocksMenuItems(
    moveBetweenBlocksDirections: MoveDirection[]
  ): OptionMenuItem[] {
    const options: OptionMenuItem[] = [
      {
        icon: 'chevron-up-line',
        title: t('move-to-first-block'),
        callback: MoveDirection.ToTopBlock,
      },
      {
        icon: 'chevron-up',
        title: t('move-to-block-above'),
        callback: MoveDirection.UpOneBlock,
      },
      {
        icon: 'chevron-down',
        title: t('move-down-one-below'),
        callback: MoveDirection.DownOneBlock,
      },
      {
        icon: 'chevron-down-line',
        title: t('move-to-last-block'),
        callback: MoveDirection.ToBottomBlock,
      },
    ];
    return options.filter(({ callback }) => moveBetweenBlocksDirections.includes(callback));
  }

  static getMoveControlOptions(): OptionMenuItem[] {
    return [
      {
        icon: 'chevron-right',
        title: t('move-within-block'),
        callback: 'move-within-block',
      },
      {
        icon: 'chevron-right',
        title: t('move-to-another-block'),
        callback: 'move-to-another-block',
      },
    ];
  }
}
